import { MASS_SPECS } from '../core/constants';
import { type Scene, type SceneCircle, traceBeam } from '../core/physics';
import { beamColor } from './palette';
import { drawMass } from './sprites';
import { hexA } from './board';

/**
 * Title background: a slow binary (a giant and a star orbiting their common centre) with a sheet
 * of parallel beams flowing past. Every beam is re-traced each frame, so the caustics move.
 */
export function drawTitleSky(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  dpr: number,
  time: number,
  reduced: boolean,
): void {
  const unit = Math.max(0.7, Math.min(1.5, Math.min(w, h) / 620));
  const portrait = h > w * 1.05;
  const cx = portrait ? w * 0.6 : w * 0.68;
  const cy = portrait ? Math.min(h * 0.21, w * 0.42) : h * 0.48;
  const t = reduced ? 1.3 : time / 1000;
  const period = 46; // seconds per orbit
  const a = (t / period) * Math.PI * 2;
  const sep = 120 * unit;
  // Mass ratio 2:1 about the barycentre.
  const g = { x: cx + Math.cos(a) * sep * 0.33, y: cy + Math.sin(a) * sep * 0.33 * 0.55 };
  const s = { x: cx - Math.cos(a) * sep * 0.67, y: cy - Math.sin(a) * sep * 0.67 * 0.55 };
  const muG = MASS_SPECS.giant.mu * unit * 1.25;
  const muS = MASS_SPECS.star.mu * unit * 1.25;
  const circles: SceneCircle[] = [
    { x: g.x, y: g.y, r: MASS_SPECS.giant.radius * unit, role: 'blocked', ref: -1 },
    { x: s.x, y: s.y, r: MASS_SPECS.star.radius * unit, role: 'blocked', ref: -1 },
  ];
  const scene: Scene = {
    masses: new Float64Array([g.x, g.y, muG, s.x, s.y, muS]),
    massCount: 2,
    circles,
    walls: [],
    nebulae: [],
    x0: -2,
    y0: -2,
    x1: w + 2,
    y1: h + 2,
  };

  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const n = portrait ? 13 : 17;
  const band = portrait ? 150 * unit : 250 * unit;
  const top = cy - band;
  const bottom = cy + band;
  const accents = new Set([Math.floor(n * 0.3), Math.floor(n * 0.62)]);
  for (let i = 0; i < n; i++) {
    const y = top + ((bottom - top) * i) / (n - 1);
    const tr = traceBeam(scene, -1, y, 1, 0, 7, { hMax: 14, maxLength: w * 1.6 + h, record: true });
    const col = accents.has(i) ? beamColor(i === Math.floor(n * 0.3) ? 1 : 4) : beamColor(7);
    const pts = tr.runs[0]!.pts;
    if (pts.length < 4) continue;
    ctx.beginPath();
    ctx.moveTo(pts[0]!, pts[1]!);
    for (let k = 2; k + 1 < pts.length; k += 2) ctx.lineTo(pts[k]!, pts[k + 1]!);
    ctx.strokeStyle = hexA(col, 0.05);
    ctx.lineWidth = 7;
    ctx.stroke();
    ctx.strokeStyle = hexA(col, accents.has(i) ? 0.7 : 0.3);
    ctx.lineWidth = accents.has(i) ? 1.5 : 1.1;
    ctx.stroke();
    if (!reduced) {
      ctx.strokeStyle = 'rgba(255,255,255,0.28)';
      ctx.lineWidth = 1.3;
      ctx.setLineDash([1.5, 30]);
      ctx.lineDashOffset = -((time * 0.05 + i * 7) % 31.5);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
  ctx.restore();
  // A soft scrim keeps the text column calm and legible.
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const scrim = portrait
    ? ctx.createLinearGradient(0, h, 0, h * 0.3)
    : ctx.createLinearGradient(0, 0, w * 0.55, 0);
  scrim.addColorStop(0, 'rgba(7, 24, 48, 0.72)');
  scrim.addColorStop(0.55, 'rgba(7, 24, 48, 0.42)');
  scrim.addColorStop(1, 'rgba(7, 24, 48, 0)');
  ctx.fillStyle = scrim;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  // Orbit path, engraved.
  ctx.strokeStyle = 'rgba(243,234,211,0.16)';
  ctx.setLineDash([2, 6]);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(cx, cy, sep * 0.67, sep * 0.67 * 0.55, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx, cy, sep * 0.33, sep * 0.33 * 0.55, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawMass(ctx, 'giant', g.x, g.y, unit, dpr);
  drawMass(ctx, 'star', s.x, s.y, unit, dpr);
  ctx.restore();
}
